// --------------------------------------------------------------------------
// Security and authentication handling functions.
// --------------------------------------------------------------------------

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';

// AuthProvider
interface AuthContextType {
  token: string | null;
  setAuthToken: (token: string, username: string) => void;
  clearAuthToken: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(sessionStorage.getItem('token'));

  // Initial log-in - set token to header
  const setAuthToken = (newToken: string, username: string) => {
    sessionStorage.setItem('token', newToken); 
    sessionStorage.setItem('username', username);
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Log-out - remove everything
  const clearAuthToken = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('username');
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ token, setAuthToken, clearAuthToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// ProtectedRoute
interface ProtectedRouteProps {
  children: JSX.Element;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token } = useAuth();
  if (!token) {
    // User not authenticated, redirect to login page
    return <Navigate to="/auth" replace />;
  }
  return children;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


// Axios instance
const axiosAuthInstance = axios.create({
  baseURL: '/api',
});

// Add a request interceptor to always include the token
axiosAuthInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axiosAuthInstance;